import React from 'react';
import { Helmet } from 'react-helmet-async';
import { PAGE_SEO } from '../data/page-seo';

// ==================== PAGE-SPECIFIC SEO CONFIGS ====================
// PAGE_SEO (titles/descriptions/keywords for every route) now lives in
// src/data/page-seo.js so it can be shared, unchanged, with the build-time
// prerender + sitemap scripts. That keeps the static HTML crawlers index on
// first fetch identical to what react-helmet renders after hydration.
// Re-exported here so existing imports (`import { PAGE_SEO } from './SEO'`) work.
export { PAGE_SEO };

// ==================== SEO COMPONENT ====================
// Handles meta tags, Open Graph, Twitter cards, and structured data
export const SEO = ({
  title = "Jamaica Schools Guide",
  description = 'Find and compare 1,650+ schools across all 14 parishes. PEP results, CSEC & CAPE exam guides, high school rankings, UWI & UTech admissions, scholarships, HEART Trust training, and career planning tools for Jamaican students and parents.',
  keywords = '',
  page = 'home',
  canonical,
  image = '/og-image.png',
  type = 'website',
  noindex = false
}) => {
  const siteName = "Jamaica Schools Guide";
  const baseUrl = 'https://jamaicaschoolsguide.com';

  // Append the site name only when the page title doesn't already contain it,
  // so titles like "Contact Us | Jamaica Schools Guide" aren't doubled up.
  const fullTitle = page === 'home'
    ? `${siteName} | PEP, CSEC & CAPE Results, School Rankings, Scholarships & Education Resources`
    : title.includes(siteName) ? title : `${title} | ${siteName}`;

  // Use clean URLs for SEO (e.g., /offences instead of /#offences)
  const url = canonical || (page === 'home' ? baseUrl : `${baseUrl}/${page}`);

  // Get page-specific keywords or use default
  const pageKeywords = keywords || PAGE_SEO[page]?.keywords || 'Jamaica schools, PEP results Jamaica, CSEC results Jamaica, CAPE results Jamaica, school rankings Jamaica, UWI admission requirements, scholarships Jamaica, HEART Trust programmes';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_JM" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />

      {/* Additional SEO */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1'} />
      <meta name="geo.region" content="JM" />
      <meta name="geo.placename" content="Jamaica" />
    </Helmet>
  );
};

// ==================== STRUCTURED DATA COMPONENTS ====================

// Website Schema
export const WebsiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: "Jamaica Schools Guide",
    alternateName: ["JM Schools Guide", "Jamaica School Guide"],
    url: 'https://jamaicaschoolsguide.com',
    description: 'Find and compare 1,650+ schools across Jamaica. PEP results, CSEC & CAPE exam guides, school rankings, scholarships, and career planning tools.',
    inLanguage: 'en-JM',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://jamaicaschoolsguide.com/schools?search={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Organization Schema
export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: "Jamaica Schools Guide",
    alternateName: ["JM Schools Guide", "Jamaica School Guide"],
    url: 'https://jamaicaschoolsguide.com',
    logo: 'https://jamaicaschoolsguide.com/logo.png',
    description: "Jamaica's most comprehensive education resource — find schools, compare rankings, understand PEP, CSEC & CAPE exams, discover scholarships, and access career planning tools across all 14 parishes.",
    email: 'info@jamaicaschoolsguide.com',
    knowsAbout: [
      'Jamaica education system', 'PEP exam Jamaica', 'CSEC examinations', 'CAPE examinations',
      'Jamaica school rankings', 'UWI admission requirements', 'UTech Jamaica', 'HEART Trust programmes',
      'Jamaica scholarships', 'CXC registration', 'Jamaica school placement', 'Sixth form Jamaica',
      'NVQ-J vocational training', 'Jamaica Ministry of Education'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@jamaicaschoolsguide.com',
      contactType: 'customer service',
      areaServed: 'JM',
      availableLanguage: ['English']
    },
    sameAs: []
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// FAQ Schema for Offences Page
export const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// LocalBusiness Schema for Directory Listings
export const LocalBusinessSchema = ({ business, type }) => {
  if (!business) return null;

  const businessType = {
    tutor: 'EducationalOrganization',
    school: 'School',
    university: 'CollegeOrUniversity',
    bookstore: 'BookStore',
    uniform: 'ClothingStore',
    supplies: 'Store'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': businessType[type] || 'LocalBusiness',
    name: business.name,
    description: business.description,
    telephone: business.phone,
    email: business.email,
    url: business.website,
    address: business.address ? {
      '@type': 'PostalAddress',
      addressLocality: business.parish,
      addressCountry: 'JM',
      streetAddress: business.address
    } : undefined,
    areaServed: business.parishes?.map(p => ({
      '@type': 'AdministrativeArea',
      name: p
    })) || [{ '@type': 'Country', name: 'Jamaica' }],
    aggregateRating: business.rating > 0 ? {
      '@type': 'AggregateRating',
      ratingValue: business.rating,
      reviewCount: business.review_count || 1
    } : undefined
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Article Schema for How-To Guides
export const ArticleSchema = ({ title, description, datePublished, dateModified }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    author: {
      '@type': 'Organization',
      name: "Jamaica Schools Guide"
    },
    publisher: {
      '@type': 'Organization',
      name: "Jamaica Schools Guide",
      logo: {
        '@type': 'ImageObject',
        url: 'https://jamaicaschoolsguide.com/logo.png'
      }
    },
    datePublished: datePublished || '2024-01-01',
    dateModified: dateModified || new Date().toISOString().split('T')[0]
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// ItemList Schema for Schools List
export const SchoolsListSchema = ({ schools, category }) => {
  if (!schools || schools.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: category ? `Jamaica Schools - ${category}` : 'Jamaica Schools Directory',
    description: 'Complete directory of schools in Jamaica with rankings, exam results, and facilities information',
    numberOfItems: schools.length,
    itemListElement: schools.slice(0, 50).map((school, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: school.name || school.title,
      description: school.description,
      url: `https://jamaicaschoolsguide.com/school-${school.id}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Government Service Schema for Official Information
export const GovernmentServiceSchema = ({ serviceName, description }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'GovernmentService',
    name: serviceName || 'Jamaica Education Information',
    description: description || 'Information about Jamaica education system, schools, and examinations',
    serviceType: 'Education Information',
    provider: {
      '@type': 'GovernmentOrganization',
      name: 'Ministry of Education Jamaica',
      url: 'https://www.moey.gov.jm'
    },
    areaServed: {
      '@type': 'Country',
      name: 'Jamaica'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// HowTo Schema for Guides (Police Stop, Learner Driver)
export const HowToSchema = ({ name, description, steps, totalTime }) => {
  if (!steps || steps.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: name,
    description: description,
    totalTime: totalTime || 'PT10M',
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name || `Step ${index + 1}`,
      text: step.text || step.description,
      url: step.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Table Schema for School Data Tables
export const TableSchema = ({ name, description, about }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Table',
    name: name,
    description: description,
    about: {
      '@type': 'Thing',
      name: about || 'Jamaica Education System'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Software Application Schema for PWA
export const SoftwareApplicationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: "Jamaica Schools Guide",
    operatingSystem: 'Web',
    applicationCategory: 'EducationalApplication',
    description: 'Find and compare 1,650+ schools across Jamaica. PEP results, CSEC & CAPE exam guides, school rankings, scholarships, HEART Trust training, and career planning tools for students and parents.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '150',
      bestRating: '5',
      worstRating: '1'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Service Area Schema for Service Providers
export const ServiceAreaSchema = ({ parishes }) => {
  if (!parishes || parishes.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Jamaica Education Services Directory',
    description: 'Find tutors, schools, and education service providers across Jamaica',
    areaServed: parishes.map(parish => ({
      '@type': 'AdministrativeArea',
      name: parish
    })),
    serviceType: 'Directory Service'
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default SEO;
